import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import compareVersions, {
    VERSION_CM_13056_REPORT_FILE_SIZE_BY_PROJECTS,
    VERSION_CM_8558_METADATA_TEMPLATE,
} from "@cores/version";
import { Tooltip, Divider, Grid, IconButton } from "@mzc-pdc/ui";
import theme from "@styles/theme";
import {
    IconArticle,
    IconAssetType,
    IconAudioType,
    IconNotice,
    IconPeopleType,
    IconPhotoType,
    IconProjects,
    IconRefresh,
    IconReply,
    IconVideoType,
} from "@mzc-cloudplex/icons";
import RecentComments from "@components_v2/dashboard/ui/recent-comments";
import ZendeskNotice from "@components_v2/dashboard/ui/zendesk-notice";
import { clearDashboardWidget, initDashboardWidget } from "@modules/dashboard";
import { useDispatch, useSelector } from "react-redux";
import fp from "lodash/fp";
import Loader from "@components/loader";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { siteMode } from "@cores/siteMode";
import { useTranslation } from "react-i18next";
// import { useAuthenticate } from "@components/dashboard/widgets/hooks";
import { WidgetTable } from "@components_v2/dashboard/ui";
import {
    AccessibleProjectsColumn,
    RecentAssetsColumn,
    RecentContentsColumn,
    RecentSharedAssetsColumn,
    RecentVideosColumn,
    WeeklyAssetCountColumn,
    WeeklyFileSizeColumn,
} from "@components_v2/dashboard/widget-table-columns";
import menuService from "@services/menuService";
import DashboardSettingPopper from "@components_v2/dashboard/ui/dashboard-setting-popper";
import useSettings from "@hooks/useSettings";
import versionedComponent from "@components/VersionedComponent";
import useCheckPermission from "@hooks/useCheckPermission";
import AccessDenied from "@routes/errors/Error403";
import i18n from "@lib/i18n";

const Dashboard = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    // const { user } = useAuthenticate(); //User Data로 project내에 있는 user 정보 조회
    const { domainStatus, getSettingData, resourceSettings } = useSettings({ type: "DomainStatus" });
    const isLibrary = siteMode.get() === "CMS";

    const stage = useSelector(({ stage }) => stage);
    const user = stage.user;
    const project = useSelector(({ project }) => project);
    const dashboard = useSelector(({ dashboard }) => dashboard.dashboardWidgetList);
    const hideContents = stage && stage.extensions && stage.extensions["HIDE_CONTENTS"] === "true";

    const DASHBOARD_PERMISSIONS = useCheckPermission({ scope: "Project", domain: "Dashboard" });

    const [isDropdownMenu, setIsDropdownMenu] = useState(false);
    const [widgets, setWidgets] = useState([]);

    const isCustomMenu = useMemo(() => {
        return resourceSettings.settings?.MenuName?.selectMenu?.value === "custom";
    }, [resourceSettings]);

    useEffect(() => {
        if (compareVersions(stage.version, VERSION_CM_8558_METADATA_TEMPLATE) >= 0) {
            dispatch(initDashboardWidget());
        }
        getSettingData("MenuName");

        return () => {
            // clear
            setIsDropdownMenu(false);
            dispatch(clearDashboardWidget());
        };
    }, [stage, project]);

    const dashboardWidgets = useMemo(() => {
        const menuServicePaths = isLibrary ? menuService.paths.library : menuService.paths;

        return [
            {
                type: "ZENDESK_TOPICS",
                color: theme.palette.etc.gray500,
                icon: <IconNotice size={10} htmlColor={theme.palette.constant.white} />,
                component: ZendeskNotice,
                priority: 1,
            },
            {
                type: "ZENDESK_SECTIONS",
                color: theme.palette.etc.gray500,
                icon: <IconNotice size={10} htmlColor={theme.palette.constant.white} />,
                component: ZendeskNotice,
                priority: 2,
            },
            {
                type: "MY_COMMENTS",
                color: theme.palette.etc.gray500,
                icon: <IconReply size={12} htmlColor={theme.palette.constant.white} />,
                component: RecentComments,
            },
            {
                type: "RECENT_MUSIC",
                resourceType: "CONTENT",
                resourceSubType: "MUSIC",
                color: theme.palette.etc.pink500,
                icon: <IconAudioType htmlColor={theme.palette.constant.white} size={12} />,
                component: WidgetTable,
                table: {
                    columns: RecentContentsColumn,
                    filter: true,
                    ...(!isLibrary &&
                        !isCustomMenu && {
                            link: menuServicePaths.contentsMusic(stage.id, project.id),
                            onClickRow: (resourceId) =>
                                menuServicePaths.contentsMusicDetail(stage.id, project.id, resourceId),
                        }),
                },
            },
            {
                type: "ACCESSIBLE_PROJECTS",
                color: theme.palette.etc.gray500,
                icon: <IconProjects htmlColor={theme.palette.constant.white} size={12} />,
                component: WidgetTable,
                table: {
                    columns: AccessibleProjectsColumn,
                    filter: false,
                },
            },
            {
                type: "RECENT_SHARED_ASSETS",
                resourceType: "ASEET",
                color: theme.palette.etc.gray500,
                icon: <IconAssetType htmlColor={theme.palette.constant.white} size={12} />,
                component: WidgetTable,
                table: {
                    columns: RecentSharedAssetsColumn,
                    filter: false,
                    ...(!isLibrary && {
                        link: !isCustomMenu ? menuServicePaths.assetsAssets(stage.id, project.id) : undefined,
                        onClickRow: (resourceId, projectId) =>
                            isCustomMenu
                                ? `${menuServicePaths.customMenuSets.assetsAssetDetail(
                                      stage.id,
                                      project.id,
                                      resourceId,
                                  )}?projectId=${projectId}`
                                : `${menuServicePaths.assetsAssetDetail(
                                      stage.id,
                                      project.id,
                                      resourceId,
                                  )}?projectId=${projectId}`,
                    }),
                },
            },
            {
                type: "RECENT_PEOPLE",
                resourceType: "CONTENT",
                resourceSubType: "PEOPLE",
                color: theme.palette.etc.purple500,
                icon: <IconPeopleType htmlColor={theme.palette.constant.white} size={12} />,
                component: WidgetTable,
                table: {
                    columns: RecentContentsColumn,
                    filter: true,
                    ...(!isLibrary &&
                        !isCustomMenu && {
                            link: menuServicePaths.contentsPeople(stage.id, project.id),
                            onClickRow: (resourceId) =>
                                menuServicePaths.contentsPeopleDetail(stage.id, project.id, resourceId),
                        }),
                },
            },
            {
                type: "RECENT_PHOTOS",
                resourceType: "CONTENT",
                resourceSubType: "PHOTO",
                color: theme.palette.etc.mint500,
                icon: <IconPhotoType htmlColor={theme.palette.constant.white} size={12} />,
                component: WidgetTable,
                table: {
                    columns: RecentContentsColumn,
                    filter: true,
                    ...(!isLibrary &&
                        !isCustomMenu && {
                            link: menuServicePaths.contentsPhoto(stage.id, project.id),
                            onClickRow: (resourceId) =>
                                menuServicePaths.contentsPhotoDetail(stage.id, project.id, resourceId),
                        }),
                },
            },
            {
                type: "RECENT_VIDEOS",
                resourceType: "CONTENT",
                resourceSubType: "VIDEO",
                color: theme.palette.primary.main,
                icon: <IconVideoType htmlColor={theme.palette.constant.white} size={12} />,
                component: WidgetTable,
                table: {
                    columns: RecentVideosColumn,
                    filter: true,
                    ...(!isLibrary &&
                        !isCustomMenu && {
                            link: menuServicePaths.contentsVideos(stage.id, project.id),
                            onClickRow: (resourceId) =>
                                menuServicePaths.contentsVideoDetail(stage.id, project.id, resourceId),
                        }),
                },
            },
            {
                type: "RECENT_ASSETS",
                resourceType: "ASSET",
                color: theme.palette.etc.purple600,
                icon: <IconAssetType htmlColor={theme.palette.constant.white} size={12} />,
                component: WidgetTable,
                table: {
                    columns: RecentAssetsColumn,
                    filter: true,
                    link: !isCustomMenu ? menuServicePaths.assetsAssets(stage.id, project.id) : undefined,
                    onClickRow: (resourceId) =>
                        isCustomMenu
                            ? `${menuServicePaths.customMenuSets.assetsAssetDetail(
                                  stage.id,
                                  project.id,
                                  resourceId,
                              )}?projectId=${project.id}`
                            : `${menuServicePaths.assetsAssetDetail(stage.id, project.id, resourceId)}?projectId=${
                                  project.id
                              }`,
                },
            },
            ...(compareVersions(stage.version, VERSION_CM_13056_REPORT_FILE_SIZE_BY_PROJECTS) >= 0
                ? [
                      {
                          type: "WEEKLY_ASSET_COUNT",
                          resourceType: "REPORT",
                          color: theme.palette.etc.gray500,
                          icon: <IconArticle htmlColor={theme.palette.constant.white} size={12} />,
                          component: WidgetTable,
                          table: {
                              columns: WeeklyAssetCountColumn,
                              filter: false,
                          },
                      },
                      {
                          type: "WEEKLY_FILE_SIZE",
                          resourceType: "REPORT",
                          color: theme.palette.etc.gray500,
                          icon: <IconArticle htmlColor={theme.palette.constant.white} size={12} />,
                          component: WidgetTable,
                          table: {
                              columns: WeeklyFileSizeColumn,
                              filter: false,
                          },
                      },
                  ]
                : []),
        ];
    }, [dashboard, stage, project, isLibrary, isCustomMenu]);

    const handleToggleWidget = useCallback(
        async (widget, checked) => {
            try {
                const localStorageWidgetsBySpace = JSON.parse(localStorage.getItem("dashboard"));
                const localStorageWidgetsIndex = localStorageWidgetsBySpace.findIndex((el) => el.stageId === stage.id);
                if (localStorageWidgetsIndex >= 0) {
                    const localStorageWidgets = localStorageWidgetsBySpace[localStorageWidgetsIndex]?.widgets || [];
                    const newLocalStorageWidgets = localStorageWidgets.map((el) =>
                        el.id === widget.id ? { ...el, isExposed: checked } : el,
                    );
                    localStorageWidgetsBySpace[localStorageWidgetsIndex] = {
                        stageId: stage.id,
                        widgets: newLocalStorageWidgets,
                    };
                    localStorage.setItem("dashboard", JSON.stringify(localStorageWidgetsBySpace));
                }

                const newWidgets = widgets.map((el) => (el.id === widget.id ? { ...el, isExposed: checked } : el));
                setWidgets(newWidgets);
            } catch (e) {
                console.error(e);
            }
        },
        [widgets],
    );

    const handleDeleteWidget = useCallback(
        (widgetName) => {
            const composer = fp.pipe(
                fp.filter((widget) => !fp.isEqual(fp.get(`name`, widget), widgetName)),
                setWidgets,
            );
            composer(widgets);
            // console.log("---------delete widget");
        },
        [widgets],
    );

    const setMoveWidgets = useCallback(
        (index, offset) => {
            const cloneWidgets = [...widgets];
            const moveIndex = index + offset;
            const newIndex = moveIndex >= cloneWidgets.length ? cloneWidgets.length - 1 : moveIndex;

            if (newIndex >= cloneWidgets.length) {
                fp.set(``, cloneWidgets.length - 1, newIndex);
            }
            setIsDropdownMenu(false);
            cloneWidgets.splice(newIndex, 0, cloneWidgets.splice(index, 1)[0]);
            setWidgets(cloneWidgets);
            // console.log("---------move widget");
        },
        [widgets],
    );
    const handleMoveWidget = useCallback(
        (sourceId, id) => {
            const sourceIndex = widgets.findIndex((widget) => widget.name === sourceId);
            const destinationIndex = widgets.findIndex((widget) => widget.name === id);
            const offset = destinationIndex - sourceIndex;
            if (sourceIndex === -1 || destinationIndex === -1) return false;
            setMoveWidgets(sourceIndex, offset);
        },
        [widgets, setMoveWidgets],
    );

    const handleRefresh = useCallback(() => {
        dispatch(initDashboardWidget());
    }, []);

    const filteredWidgets = useMemo(
        () =>
            dashboardWidgets?.filter((widget) => {
                if (hideContents || isLibrary || isCustomMenu) {
                    if (
                        ["RECENT_MUSIC", "RECENT_PEOPLE", "RECENT_PHOTOS", "RECENT_VIDEOS"].some(
                            (n) => n === widget.type,
                        )
                    ) {
                        return false;
                    }
                }
                if (domainStatus.COMMENTS_OFF) {
                    if (widget.type === "MY_COMMENTS") {
                        return false;
                    }
                }
                if (isLibrary) {
                    if (
                        ["RECENT_SHARED_ASSETS", "WEEKLY_ASSET_COUNT", "WEEKLY_FILE_SIZE", "RECENT_SHARED_ASSETS"].some(
                            (n) => n === widget.type,
                        )
                    ) {
                        return false;
                    }
                }
                return true;
            }),
        [dashboard, stage, project, isLibrary, isCustomMenu],
    );

    const exposedWidgets = useMemo(() => {
        return widgets?.filter((widget) => widget.isExposed);
    }, [widgets]);

    useEffect(() => {
        if (!dashboard.data?.length || dashboard.pending) return;

        let localStorageWidgetsBySpace = JSON.parse(localStorage.getItem("dashboard")) || [];

        let localStorageWidgetsIndex = localStorageWidgetsBySpace.findIndex((el) => el.stageId === stage.id);

        let widgetIndex = 0;
        if (localStorageWidgetsBySpace.length > 0) {
            if (localStorageWidgetsIndex < 0) widgetIndex = localStorageWidgetsBySpace.length;
            else widgetIndex = localStorageWidgetsIndex;
        }

        const localStorageWidgets = localStorageWidgetsBySpace[localStorageWidgetsIndex]?.widgets;

        const parseWidget = (el) => {
            if (!localStorageWidgets) return el;
            const localStorageWidget = localStorageWidgets?.find((widget) => widget.id === el.id);
            // https://mzdevs.atlassian.net/browse/CM-11542
            const isExposed = el.isExposeEditable ? localStorageWidget?.isExposed ?? el.isExposed : el.isExposed;
            if (localStorageWidget) return { ...el, isExposed };
            else return el;
        };

        const stateWidgets = dashboard.data
            ?.filter((el) => filteredWidgets.findIndex((widget) => el.type === widget.type) >= 0)
            ?.sort((a, b) => b.fullWidth - a.fullWidth)
            .map((el) => parseWidget(el));
        if (localStorageWidgets) {
        }

        const parsedWidgets = {
            spaceId: stage.id,
            projectId: project.id,
            widgets: stateWidgets.map((widg) => {
                const widgetStyle = dashboardWidgets?.find((el) => el.type === widg.type) ?? {};
                const isNotice = ["ZENDESK_TOPICS", "ZENDESK_SECTIONS"].includes(widg.type) ? true : false;

                return {
                    ...widgetStyle,
                    id: widg.id,
                    name: widg.name,
                    title: widg.name,
                    params: widg.params,
                    logo: widg.logoUrl,
                    viewAllUrl: widg.viewAllUrl,
                    isExposeEditable: widg.isExposeEditable,
                    isExposed: widg.isExposed,
                    fullWidth: false,
                    isNotice: isNotice,
                    priority: widgetStyle.priority ?? stateWidgets.length,
                };
            }),
        };
        parsedWidgets.widgets.sort((a, b) => a.priority - b.priority);
        setWidgets(parsedWidgets.widgets);

        const widgetsForLocalStorage = parsedWidgets.widgets.map(({ id, type, isExposed }) => ({
            id,
            type,
            isExposed,
        }));

        localStorageWidgetsBySpace[widgetIndex] = { stageId: stage.id, widgets: widgetsForLocalStorage };

        localStorage.setItem("dashboard", JSON.stringify([...localStorageWidgetsBySpace]));
    }, [dashboard, stage, project, isLibrary]);

    return (
        <>
            {!DASHBOARD_PERMISSIONS.READ ? (
                <AccessDenied />
            ) : (
                <Grid>
                    <Grid
                        item
                        sx={{
                            position: "relative",
                            zIndex: 10,
                            display: "flex",
                            justifyContent: "space-between",
                            padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
                            backgroundColor: theme.palette.background.default,
                            borderBottom: `1px solid ${theme.palette.greySecondary[100]}`,
                            mt: theme.spacing(-2),
                            mx: theme.spacing(-5),
                            mb: theme.spacing(3),
                        }}
                    >
                        <Grid container sx={{ justifyContent: "flex-end" }}>
                            <DashboardSettingPopper
                                open={isDropdownMenu}
                                widgets={widgets}
                                selected={exposedWidgets}
                                onChangeToggle={(value) => setIsDropdownMenu(value)}
                                onClose={() => setIsDropdownMenu(false)}
                                onClickToggle={handleToggleWidget}
                            />
                            <Divider orientation="vertical" flexItem sx={{ mx: theme.spacing(1.5) }} />
                            <Tooltip title={i18n.t(`common::label::Refresh`, `Refresh`)}>
                                <IconButton circled onClick={handleRefresh}>
                                    <IconRefresh size={16} />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>
                    <div className={`dashboard-body`}>
                        <Grid container className={"widgets"} gap={3} justifyContent={"flex-start"}>
                            {dashboard.pending ? (
                                <Loader />
                            ) : (
                                <DndProvider backend={HTML5Backend}>
                                    {fp.map((widget) => {
                                        const { component: Widget, table: tableProps, ...props } = widget;
                                        return (
                                            <Widget
                                                key={widget.id}
                                                user={user}
                                                {...props}
                                                {...tableProps}
                                                removable={!fp.isEqual(1, widgets.length)}
                                                blockDrag={widget.fullWidth || widget.isNotice}
                                                onMove={handleMoveWidget}
                                                onDelete={handleDeleteWidget}
                                                isCustomMenu={isCustomMenu}
                                            />
                                        );
                                    }, exposedWidgets)}
                                </DndProvider>
                            )}
                        </Grid>
                    </div>
                </Grid>
            )}
        </>
    );
};

export default Dashboard;
